import { useMemo, useState } from 'react'
import { ReceiptText } from 'lucide-react'
import type { DerivedDC } from '@/api/distributionCenters'
import type { WavePlanDcVendorTotals } from '@/api/wavePlans'
import { cn } from '@/utils/cn'
import { dcCost, dcLabel, dcUnits, NO_VENDOR } from './dc-model'
import { ALERT_BADGE } from './DcAllocationGrid'
import { DcScopeToggle } from './DcWaveSummary'
import { ColumnResizer } from './ColumnResizer'
import { useColumnSizing, type SizedColumn } from './columnSizing'

interface Props {
  centers: DerivedDC[]
  /** The selected wave's PO rollup by vendor and DC. */
  wave: WavePlanDcVendorTotals[]
  /** The same over every visible wave. */
  allWaves: WavePlanDcVendorTotals[]
  waveLabel: string
}

/**
 * PO cost by vendor and DC: the rounded DC units at the captured unit cost, a
 * PIAB display's packs at its own cost (its contents are not costed twice).
 */
export function DcVendorPoSummary({
  centers,
  wave,
  allWaves,
  waveLabel,
}: Props) {
  const [all, setAll] = useState(false)
  const vendors = all ? allWaves : wave
  // The DC columns share any extra width.
  const columns = useMemo<SizedColumn[]>(
    () => [
      { key: 'vendor', width: 240, minWidth: 140 },
      { key: 'moa', width: 100, minWidth: 80 },
      ...centers.map(dc => ({ key: dc.id, width: 120, minWidth: 80 })),
      { key: 'total', width: 130, minWidth: 100 },
    ],
    [centers]
  )
  const fill = useMemo(() => centers.map(dc => dc.id), [centers])
  const sizing = useColumnSizing(columns, fill)
  const resizer = (key: string, label: string) => (
    <ColumnResizer
      label={label}
      width={sizing.widths[key]}
      onResize={width => sizing.resize(key, width)}
      onReset={() => sizing.reset(key)}
    />
  )
  const centerTotal = (dcId: string, field: 'cost' | 'units') =>
    vendors.reduce(
      (sum, vendor) =>
        sum + (vendor.centers.find(item => item.dc_id === dcId)?.[field] ?? 0),
      0
    )
  const cost = vendors.reduce((sum, vendor) => sum + vendor.cost, 0)
  const units = vendors.reduce((sum, vendor) => sum + vendor.units, 0)
  const missing = vendors.reduce(
    (sum, vendor) => sum + vendor.cost_missing_count,
    0
  )
  return (
    <section
      role="region"
      aria-label="PO cost by vendor and DC"
      className="rounded-lg border bg-card"
      data-testid="dc-po-summary"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
        <div>
          <h2 className="flex items-center gap-2 text-xs font-semibold">
            <ReceiptText size={14} />
            PO cost by vendor and DC{' '}
            <span className="font-normal text-muted-foreground">
              · {all ? 'all waves' : waveLabel}
            </span>
          </h2>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Rounded DC units at the captured unit cost; a PIAB display's packs
            at the display's cost (its contents are not costed twice). Units
            exclude Parent lines. Below MOA: the vendor's PO at the DC is under
            the vendor's MOA (an alert; nothing is added).
          </p>
        </div>
        <DcScopeToggle
          label="PO summary scope"
          waveOption="This wave"
          all={all}
          onChange={setAll}
        />
      </div>
      <div ref={sizing.containerRef} className="overflow-x-auto">
        <table
          className="dc-po-table text-xs tabular-nums"
          style={{ width: sizing.tableWidth }}
        >
          <colgroup>
            {columns.map(column => (
              <col
                key={column.key}
                data-column={column.key}
                style={{ width: sizing.widths[column.key] }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">
                Vendor
                {resizer('vendor', 'Vendor')}
              </th>
              <th
                scope="col"
                title="The vendor's MOA, from its lines' MOAs (the most frequent by default; see the calculation parameters)"
              >
                MOA
                {resizer('moa', 'MOA')}
              </th>
              {centers.map(dc => (
                <th scope="col" key={dc.id} title={dcLabel(dc)}>
                  {dc.code}
                  {dc.name && dc.name !== dc.code && (
                    <span className="dc-center-name">{dc.name}</span>
                  )}
                  {resizer(dc.id, dcLabel(dc))}
                </th>
              ))}
              <th scope="col">
                Total
                {resizer('total', 'Total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {vendors.map(vendor => {
              const name = vendor.vendor_name || vendor.vendor_code || NO_VENDOR
              const moa = vendor.moa ?? null
              return (
                <tr
                  key={vendor.key}
                  data-testid={`dc-po-${vendor.key || 'none'}`}
                >
                  <th scope="row">
                    {name}
                    <span className="block text-[10px] font-normal text-muted-foreground">
                      {vendor.line_count}{' '}
                      {vendor.line_count === 1 ? 'line' : 'lines'}
                      {vendor.cost_missing_count > 0 &&
                        ` · ${vendor.cost_missing_count} not costed`}
                    </span>
                  </th>
                  <td data-testid="dc-po-moa">
                    {moa != null ? (
                      dcCost(moa)
                    ) : (
                      <span
                        className="text-muted-foreground"
                        title="No MOA for this vendor"
                      >
                        —
                      </span>
                    )}
                  </td>
                  {centers.map(dc => {
                    const item = vendor.centers.find(
                      center => center.dc_id === dc.id
                    )
                    const below = moa != null ? (item?.below_moa_waves ?? 0) : 0
                    const unchecked =
                      moa != null ? (item?.moa_unchecked_waves ?? 0) : 0
                    return (
                      <td
                        key={dc.id}
                        className={below > 0 ? 'bg-amber-50' : undefined}
                      >
                        {item && (item.cost > 0 || item.units > 0) ? (
                          <>
                            <strong>{dcCost(item.cost)}</strong>
                            <span className="block text-[10px] text-muted-foreground">
                              {dcUnits(item.units)} units
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {below > 0 && moa != null && (
                          <span
                            className={cn(ALERT_BADGE, 'whitespace-normal')}
                            data-testid={`dc-po-below-moa-${dc.id}`}
                            title={
                              all
                                ? `${name}'s PO at ${dcLabel(dc)} is below the vendor MOA of ${dcCost(moa)} in ${below} of the waves.`
                                : `${name}'s PO at ${dcLabel(dc)} is ${dcCost(item?.cost ?? 0)}, below the vendor MOA of ${dcCost(moa)}.`
                            }
                          >
                            {all
                              ? `${below} ${below === 1 ? 'wave' : 'waves'} below MOA`
                              : 'Below MOA'}
                          </span>
                        )}
                        {unchecked > 0 && (
                          <span
                            className="block text-[10px] text-muted-foreground"
                            title="A line with units has no cost, so this PO is not checked against the MOA."
                          >
                            {all
                              ? `${unchecked} ${unchecked === 1 ? 'wave' : 'waves'} not checked`
                              : 'MOA not checked'}
                          </span>
                        )}
                      </td>
                    )
                  })}
                  <td>
                    <strong>{dcCost(vendor.cost)}</strong>
                    <span className="block text-[10px] text-muted-foreground">
                      {dcUnits(vendor.units)} units
                    </span>
                  </td>
                </tr>
              )
            })}
            {!vendors.length && (
              <tr>
                <td
                  colSpan={centers.length + 3}
                  className="py-6 text-center text-muted-foreground"
                >
                  No units planned for any vendor.
                </td>
              </tr>
            )}
          </tbody>
          {vendors.length > 0 && (
            <tfoot>
              <tr>
                <th scope="row">All vendors</th>
                <td />
                {centers.map(dc => (
                  <td key={dc.id}>
                    <strong>{dcCost(centerTotal(dc.id, 'cost'))}</strong>
                    <span className="block text-[10px] text-muted-foreground">
                      {dcUnits(centerTotal(dc.id, 'units'))} units
                    </span>
                  </td>
                ))}
                <td>
                  <strong data-testid="dc-po-total">{dcCost(cost)}</strong>
                  <span className="block text-[10px] text-muted-foreground">
                    {dcUnits(units)} units
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {missing > 0 && (
        <p className="border-t px-4 py-2 text-[11px] text-amber-800">
          {missing} {missing === 1 ? 'line has' : 'lines have'} units without a
          PO cost: no unit cost, or a PIAB component whose units per display are
          unknown. Their units are counted, their cost is not.
        </p>
      )}
    </section>
  )
}
